const DEFAULT_EVENTS = [
    'mousemove',
    'mousedown',
    'keydown',
    'keypress',
    'scroll',
    'touchstart',
    'touchmove',
    'click',
    'visibilitychange',
];
const DEFAULT_TIMEOUT = 3000; // 3 seconds
/**
 * HCPInactivityDetector
 *
 * A class that detects when the user is inactive on the page.
 * Monitors mouse movement, scroll, keyboard activity, touch events,
 * clicks, and visibility changes.
 *
 * @example
 * const inactivityDetector = new HCPInactivityDetector({
 *   onInactive: () => {
 *     console.log('The user is inactive');
 *   },
 *   onActive: () => {
 *     console.log('The user is active again');
 *   },
 * });
 */
class HCPInactivityDetector {
    constructor(options = {}) {
        this.timeoutId = null;
        this.isInactive = false;
        this.isRunning = false;
        this.timeoutDelay = options.timeoutDelay ?? DEFAULT_TIMEOUT;
        this.onInactive = options.onInactive ?? null;
        this.onActive = options.onActive ?? null;
        this.events = options.events ?? DEFAULT_EVENTS;
        this.boundHandleActivity = this.handleActivity.bind(this);
        this.boundHandleVisibilityChange = this.handleVisibilityChange.bind(this);
        if (options.autoStart !== false) {
            this.start();
        }
    }
    /**
     * Starts monitoring for user inactivity
     */
    start() {
        if (this.isRunning) {
            return;
        }
        this.isRunning = true;
        this.isInactive = false;
        this.addEventListeners();
        this.resetTimer();
    }
    /**
     * Stops monitoring for user inactivity
     */
    stop() {
        if (!this.isRunning) {
            return;
        }
        this.isRunning = false;
        this.clearTimer();
        this.removeEventListeners();
    }
    /**
     * Returns whether the user is currently inactive
     */
    getIsInactive() {
        return this.isInactive;
    }
    /**
     * Returns whether the detector is currently running
     */
    getIsRunning() {
        return this.isRunning;
    }
    /**
     * Updates the delay value
     * @param delay - New delay in milliseconds
     */
    setTimeoutDelay(delay) {
        this.timeoutDelay = delay;
        if (this.isRunning && !this.isInactive) {
            this.resetTimer();
        }
    }
    /**
     * Gets the current delay value
     */
    getTimeoutDelay() {
        return this.timeoutDelay;
    }
    /**
     * Manually triggers the active state
     */
    triggerActive() {
        this.handleActivity();
    }
    /**
     * Destroys the detector and cleans up all listeners
     */
    destroy() {
        this.stop();
        this.onInactive = null;
        this.onActive = null;
    }
    /**
     * Adds event listeners for all activity events
     */
    addEventListeners() {
        this.events.forEach((event) => {
            if (event === 'scroll') {
                window.addEventListener(event, this.boundHandleActivity, { passive: true });
            }
            else if (event === 'visibilitychange') {
                document.addEventListener(event, this.boundHandleVisibilityChange, { passive: true });
            }
            else {
                document.addEventListener(event, this.boundHandleActivity, { passive: true });
            }
        });
    }
    /**
     * Removes all event listeners
     */
    removeEventListeners() {
        this.events.forEach((event) => {
            if (event === 'scroll') {
                window.removeEventListener(event, this.boundHandleActivity);
            }
            else if (event === 'visibilitychange') {
                document.removeEventListener(event, this.boundHandleVisibilityChange);
            }
            else {
                document.removeEventListener(event, this.boundHandleActivity);
            }
        });
    }
    /**
     * Handles any activity event
     */
    handleActivity() {
        if (!this.isRunning) {
            return;
        }
        // If user was inactive, trigger active callback
        if (this.isInactive) {
            this.isInactive = false;
            this.onActive?.();
        }
        this.resetTimer();
    }
    /**
     * Handles visibility change events (tab switching)
     */
    handleVisibilityChange() {
        if (!this.isRunning) {
            return;
        }
        if (document.hidden) {
            // User switched away from tab - consider inactive immediately
            this.clearTimer();
            if (!this.isInactive) {
                this.isInactive = true;
                this.onInactive?.();
            }
        }
        else {
            // User came back to tab - consider active
            this.handleActivity();
        }
    }
    /**
     * Resets the inactivity timer
     */
    resetTimer() {
        this.clearTimer();
        this.timeoutId = setTimeout(() => {
            this.isInactive = true;
            this.onInactive?.();
        }, this.timeoutDelay);
    }
    /**
     * Clears the current timer
     */
    clearTimer() {
        if (this.timeoutId !== null) {
            clearTimeout(this.timeoutId);
            this.timeoutId = null;
        }
    }
}
export { HCPInactivityDetector };
